// src/components/ThreadGutter.jsx
"use client";

import Link from "next/link";
import { memo, useCallback } from "react";
import { getReplyRailAction, REPLY_RAIL_ACTIONS } from "@/lib/replyRailAction.mjs";

function ThreadGutter({
  avatar,
  avatarPending = false,
  alt = "",
  replyCount = 0,
  size = "md",
  onOpenChildren,
  onReply,
  profileHref,
  onAvatarNavigate,
}) {
  const railAction = getReplyRailAction(replyCount);
  const hasReplies = railAction === REPLY_RAIL_ACTIONS.OPEN_CHILDREN;

  const handleReply = useCallback(
    (event) => {
      event?.stopPropagation?.();
      if (typeof onReply === "function") onReply();
    },
    [onReply]
  );

  const handleRailClick = useCallback(
    (event) => {
      event?.stopPropagation?.();
      if (railAction === REPLY_RAIL_ACTIONS.OPEN_CHILDREN) {
        onOpenChildren?.();
        return;
      }
      onReply?.();
    },
    [onOpenChildren, onReply, railAction]
  );

  return (
    <div className={`thread-gutter thread-gutter--${size}`}>
      <AvatarLink
        avatar={avatar}
        pending={avatarPending}
        alt={alt}
        href={profileHref}
        onReply={handleReply}
        onAvatarNavigate={onAvatarNavigate}
      />
      <button
        type="button"
        className={`thread-gutter__rail${hasReplies ? "" : " thread-gutter__rail--empty"}`}
        aria-label={hasReplies ? `View ${replyCount} replies` : "Reply"}
        onClick={handleRailClick}
      >
        {hasReplies ? (
          <>
            <span className="thread-gutter__line" />
            <span className="thread-gutter__terminus">{formatCount(replyCount)}</span>
          </>
        ) : null}
      </button>
    </div>
  );
}

export default memo(ThreadGutter);

function formatCount(count = 0) {
  if (count <= 0) return "";
  if (count > 99) return "99+";
  return `+${count}`;
}

function AvatarLink({ avatar, alt, href, onReply, onAvatarNavigate, pending }) {
  if (pending) {
    return (
      <span
        className="thread-gutter__avatar bg-[var(--surface-2)]"
        aria-hidden="true"
      />
    );
  }

  const img = (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={avatar} alt={alt} />
  );
  if (!href) {
    return (
      <button
        type="button"
        className="thread-gutter__avatar pointer-events-auto"
        onClick={onReply}
        aria-label={alt ? `Reply to ${alt.replace(/ avatar$/, "")}` : "Reply"}
      >
        {img}
      </button>
    );
  }
  return (
    <Link
      href={href}
      className="thread-gutter__avatar pointer-events-auto"
      onClick={(e) => {
        onAvatarNavigate?.();
        e.stopPropagation();
      }}
      aria-label={alt || "Profile"}
    >
      {img}
    </Link>
  );
}
